// El flujo de login del pool administrativo, sin marcado.
//
// Lo que se comparte entre SPAs es la maquina de estados de Cognito, que es
// donde estan los errores caros: los usuarios los crea un admin, asi que el
// primer login recorre
//   NEW_PASSWORD_REQUIRED -> MFA_SETUP (asociar software token) -> sesion
// y los siguientes SOFTWARE_TOKEN_MFA (codigo de 6 digitos). Con el pool en
// OPTIONAL (ADR-024) una cuenta sin TOTP en una SPA que lo exige recorre
//   sesion retenida -> mfa_enrolamiento -> credenciales (con aviso) -> TOTP.
//
// Lo que NO se comparte es el marcado: la torre y el portal del proveedor son
// dos superficies distintas (una sala de control oscura y un portal claro
// para un tercero, ver gestion/07_identidad) y cada una lo dibuja con sus
// propios componentes. Un componente unico obligaria a pasarle nombres de
// clase por props, que es peor que esto.
import { useCallback, useMemo, useState } from "react";
import { ClienteCognito, TOTP_OBLIGATORIO } from "./cognito.js";
/** Aviso tras enrolar el TOTP (ADR-024): la sesion sin TOTP se descarto. */
export const AVISO_REINGRESAR = "Listo, el segundo factor quedó activado. Ingresá de nuevo: esta vez te va a pedir el código.";
/** El secreto TOTP en grupos de 4 para tipearlo sin perderse. */
export function agruparSecreto(secreto) {
    return secreto.replace(/(.{4})/g, "$1 ").trim();
}
/**
 * `branding` y `politica` tienen que ser estables (constantes del modulo): un
 * objeto literal nuevo por render recrearia el cliente de Cognito y perderia
 * el challenge en curso. Sin `politica`, la SPA exige TOTP.
 */
export function useLoginFlow(config, branding, onLogin, politica = TOTP_OBLIGATORIO) {
    const cliente = useMemo(() => new ClienteCognito({ userPoolId: config.userPoolId, clientId: config.clientId }, branding, politica), [config.userPoolId, config.clientId, branding, politica]);
    const [pantalla, setPantalla] = useState({ paso: "credenciales" });
    const [error, setError] = useState(null);
    const [aviso, setAviso] = useState(null);
    const [sending, setEnviando] = useState(false);
    const execute = useCallback((action) => {
        setEnviando(true);
        setError(null);
        setAviso(null);
        action()
            .then((paso) => {
            setError(null);
            switch (paso.type) {
                case "sesion":
                    onLogin(paso.session);
                    break;
                case "nueva_contrasena":
                    setPantalla({ paso: "nueva_contrasena" });
                    break;
                case "mfa_setup":
                    setPantalla({
                        paso: "mfa_setup",
                        secreto: paso.secreto,
                        urlOtpauth: paso.urlOtpauth,
                    });
                    break;
                case "mfa_codigo":
                    setPantalla({ paso: "mfa_codigo" });
                    break;
                case "mfa_enrolamiento":
                    setPantalla({
                        paso: "mfa_enrolamiento",
                        secreto: paso.secreto,
                        urlOtpauth: paso.urlOtpauth,
                    });
                    break;
                case "reingresar":
                    setPantalla({ paso: "credenciales" });
                    setAviso(AVISO_REINGRESAR);
                    break;
            }
        })
            .catch((cause) => {
            setError(cause instanceof Error ? cause.message : "Error inesperado al iniciar sesion.");
        })
            .finally(() => {
            setEnviando(false);
        });
    }, [onLogin]);
    return {
        pantalla,
        error,
        aviso,
        sending,
        iniciarSesion: useCallback((email, contrasena) => {
            execute(() => cliente.iniciarSesion(email.trim(), contrasena));
        }, [cliente, execute]),
        completarNuevaContrasena: useCallback((nuevaContrasena) => {
            execute(() => cliente.completarNuevaContrasena(nuevaContrasena));
        }, [cliente, execute]),
        verificarTotpInicial: useCallback((codigo) => {
            execute(() => cliente.verificarTotpInicial(codigo.trim()));
        }, [cliente, execute]),
        responderCodigoMfa: useCallback((codigo) => {
            execute(() => cliente.responderCodigoMfa(codigo.trim()));
        }, [cliente, execute]),
        confirmarEnrolamiento: useCallback((codigo) => {
            execute(() => cliente.confirmarEnrolamiento(codigo.trim()));
        }, [cliente, execute]),
        volverAlInicio: useCallback(() => {
            setPantalla({ paso: "credenciales" });
            setError(null);
            setAviso(null);
        }, []),
    };
}
//# sourceMappingURL=useLogin.js.map